/**
 * aiOverviewStore
 *
 * Drives the AI Overview UI block on the search results page. Owns the
 * streaming state for one in-flight `/v1/summarize` request at a time and
 * exposes a small set of selectors so each subcomponent (overview text,
 * per-listing card, suggested searches) re-renders only when its slice
 * changes.
 *
 * Design notes:
 * - Per-listing state is keyed by `oafId` because the contract does not
 *   guarantee listing summaries arrive in request order.
 * - The current request's id and AbortController live as module-local
 *   control-plane state — keeping them out of the immer-produced state
 *   avoids freezing AbortController instances and keeps view state
 *   serializable.
 * - The store does not retry on `error`; the contract recommends a manual
 *   "Try again" affordance, and immediate retries are unlikely to help.
 * - Completed summaries are cached in an in-state LRU (`state.summaryCache`)
 *   keyed by the caller-supplied search-context key so that navigating away
 *   to a listing and returning reuses the cached summary instead of
 *   re-issuing the SSE request. The cache lives in state (matching
 *   `searchStore.searchResults`) so it is visible to `getState()`. Errors
 *   are not cached — retry must still hit the service.
 */
import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import {
    AI_OVERVIEW_ERROR_TOKEN_FALLBACK,
    AiOverviewErrorToken,
} from '~/modules/analytics/datacloudEventTypes/constants';
import {
    SummarizeHttpError,
    SummarizeListing,
    SummarizeRequest,
    SummarizeStreamEvent,
    summarize,
} from '~/modules/services/summarizeService';
import { randomUUID } from '~/modules/utilities/cross_env_utils';
import { logger } from '~/modules/utilities/logger';

export type AiOverviewStatus = 'idle' | 'streaming' | 'done' | 'error' | 'truncated';

export interface AiListingSummaryState {
    /** Original index in the request — used to render placeholders in stable order. */
    index: number;
    text: string;
    done: boolean;
}

/** The view slice of the store — everything except the cache itself. */
export interface AiOverviewSnapshot {
    /** Echoed back to the UI so the heading shows the user's original query. */
    searchKeyword: string;
    /** Listing inputs for the current request, in request order. */
    listings: SummarizeListing[];
    overview: string;
    overviewDone: boolean;
    perListing: Record<string, AiListingSummaryState>;
    nextQueries: string[];
    status: AiOverviewStatus;
    /** Mapped UX-ready error token; never the raw server "Service unavailable" string. */
    errorMessage?: string;
    /**
     * Raw server error text from the terminal SSE `error` event. It is retained
     * for compatibility but never rendered or emitted in telemetry.
     */
    errorDetail?: string;
    /**
     * Data Cloud failure taxonomy for the retry event. Derived from the HTTP
     * status when the request fails with one; 'unknown' for opaque failures
     * (SSE error event, network drop). Undefined until a failure occurs.
     */
    errorToken?: AiOverviewErrorToken;
    /**
     * Correlation id sent to `/v1/summarize` for this stream and echoed onto
     * every Data Cloud row (impression, feedback, suggested/card click,
     * streamComplete, streamError, expand, mobileToggle, retry) for this
     * result set so summarize + telemetry records can be stitched together
     * server-side. Regenerated per fresh stream; cached snapshots keep the
     * id of the stream that produced them.
     */
    correlationId: string;
    /** Stream-start → first summary chunk, in ms. Undefined until the first chunk lands. */
    ttftMs?: number;
    /**
     * Estimated token total of the overview stream. The service sends no
     * usage field, so this is a client-side approximation derived from the
     * accumulated overview text; it updates as chunks land and is final once
     * the stream terminates. Undefined until the first chunk lands.
     */
    tokenCount?: number;
    /**
     * Stream-start → terminal state (done/truncated/error), in ms. Undefined
     * until the stream reaches a terminal state.
     */
    latencyMs?: number;
}

export interface AiOverviewState extends AiOverviewSnapshot, State {
    /**
     * LRU cache of completed summaries, keyed by the caller-supplied
     * search-context key. Insertion order on Map gives LRU for free —
     * `get` re-inserts on hit, `set` evicts the oldest key when capacity
     * is exceeded. Lives inside state (rather than a module-local) so it
     * is visible to `getState()` and matches `searchStore.searchResults`.
     * Survives `reset()` so back-nav from a listing still hits the cache.
     */
    summaryCache: Map<string, AiOverviewSnapshot>;
}

const SUMMARY_CACHE_CAPACITY = 20;

/**
 * Client-side token estimate. The summarize service does not report token
 * usage, so we approximate from character count using the ~4-chars-per-token
 * heuristic that holds for English/Japanese marketplace copy. Used only for
 * Data Cloud telemetry throughput KPIs, not for any product behaviour.
 */
const estimateTokens = (text: string): number => Math.ceil(text.length / 4);

/**
 * Map a failure to the Data Cloud error taxonomy. Only HTTP-status failures
 * carry enough signal to classify; the SSE `error` event and opaque transport
 * failures (network drop, CORS, blocked request) have no status and fall back
 * to 'unknown'. The 408/429/5xx buckets follow the summarize API contract.
 */
const errorTokenFromFailure = (err: unknown): AiOverviewErrorToken => {
    if (!(err instanceof SummarizeHttpError)) {
        return AI_OVERVIEW_ERROR_TOKEN_FALLBACK;
    }
    if (err.status === 429) {
        return 'rate_limited';
    }
    if (err.status === 408 || err.status === 504) {
        return 'upstream_timeout';
    }
    if (err.status === 400 || err.status === 422) {
        return 'validation_error';
    }
    if (err.status >= 500) {
        return 'service_unavailable';
    }
    return AI_OVERVIEW_ERROR_TOKEN_FALLBACK;
};

const baseViewState = (): AiOverviewSnapshot => ({
    searchKeyword: '',
    listings: [],
    overview: '',
    overviewDone: false,
    perListing: {},
    nextQueries: [],
    status: 'idle',
    errorMessage: undefined,
    errorDetail: undefined,
    errorToken: undefined,
    correlationId: '',
    ttftMs: undefined,
    tokenCount: undefined,
    latencyMs: undefined,
});

const baseInitialState = (): AiOverviewState => ({
    ...baseViewState(),
    summaryCache: new Map(),
});

// Control-plane state. Lives outside the immer-produced view state so
// AbortController instances are not frozen and the current-request id is
// not snapshotted into stale observer payloads.
let activeRequestId = 0;
let activeAbort: AbortController | undefined;
// Monotonic timestamp of the active stream's start, for TTFT/latency timing.
let streamStartedAt: number | undefined;

const elapsedSinceStreamStart = (): number | undefined => {
    if (streamStartedAt === undefined) {
        return undefined;
    }
    return performance.now() - streamStartedAt;
};

/**
 * LRU write into the immer-draft cache. Reinserts on hit; evicts oldest key
 * when capacity is exceeded. Must be called from inside a `set()` producer.
 */
const cacheWrite = (cache: Map<string, AiOverviewSnapshot>, key: string, value: AiOverviewSnapshot): void => {
    if (cache.has(key)) {
        cache.delete(key);
    } else if (cache.size >= SUMMARY_CACHE_CAPACITY) {
        const oldest = cache.keys().next().value;
        if (oldest !== undefined) {
            cache.delete(oldest);
        }
    }
    cache.set(key, value);
};

const snapshotForCache = (state: AiOverviewState): AiOverviewSnapshot | undefined => {
    if (state.status !== 'done' && state.status !== 'truncated') {
        return undefined;
    }
    return {
        searchKeyword: state.searchKeyword,
        listings: state.listings,
        overview: state.overview,
        overviewDone: state.overviewDone,
        perListing: state.perListing,
        nextQueries: state.nextQueries,
        status: state.status,
        errorMessage: undefined,
        correlationId: state.correlationId,
        ttftMs: state.ttftMs,
        tokenCount: state.tokenCount,
        latencyMs: state.latencyMs,
    };
};

const initPerListing = (listings: SummarizeListing[]): Record<string, AiListingSummaryState> => {
    const out: Record<string, AiListingSummaryState> = {};
    for (let i = 0; i < listings.length; i++) {
        out[listings[i].oafId] = { index: i, text: '', done: false };
    }
    return out;
};

export const aiOverviewStore = createStore(
    baseInitialState(),
    (set: SetState<AiOverviewState>, get: GetState<AiOverviewState>) => ({
        /**
         * Kick off (or reuse) a summarize stream for `req`.
         *
         * When `cacheKey` is supplied and the store has previously completed
         * a summary for that key, the state is re-hydrated from the cache
         * and no network request is made. This is what lets a user navigate
         * to a listing and come back to the same search results without
         * re-generating the AI summary. Callers build the key from the full
         * search context (query, marketplace/tab, filters, sort, locale).
         */
        async start(req: SummarizeRequest, cacheKey?: string): Promise<void> {
            // Abort any prior in-flight request before issuing the new one.
            // The contract notes the service has no per-user serialization,
            // so the client must keep at most one stream active.
            if (activeAbort) {
                activeAbort.abort();
                activeAbort = undefined;
            }

            const requestId = ++activeRequestId;

            if (cacheKey) {
                const cached = get().summaryCache.get(cacheKey);
                if (cached) {
                    set(function hydrateFromCache(draft: AiOverviewState) {
                        // LRU touch — reinsert to mark most-recently-used.
                        draft.summaryCache.delete(cacheKey);
                        draft.summaryCache.set(cacheKey, cached);
                        draft.searchKeyword = cached.searchKeyword;
                        draft.listings = cached.listings;
                        draft.overview = cached.overview;
                        draft.overviewDone = cached.overviewDone;
                        draft.perListing = cached.perListing;
                        draft.nextQueries = cached.nextQueries;
                        draft.status = cached.status;
                        draft.errorMessage = undefined;
                        draft.errorDetail = undefined;
                        draft.correlationId = cached.correlationId;
                        draft.ttftMs = cached.ttftMs;
                        draft.tokenCount = cached.tokenCount;
                        draft.latencyMs = cached.latencyMs;
                    });
                    return;
                }
            }

            const abort = new AbortController();
            activeAbort = abort;
            const correlationId = randomUUID();
            streamStartedAt = performance.now();

            set(function startSummarize(draft: AiOverviewState) {
                draft.searchKeyword = req.searchQuery;
                draft.listings = req.listings;
                draft.overview = '';
                draft.overviewDone = false;
                draft.perListing = initPerListing(req.listings);
                draft.nextQueries = [];
                draft.status = 'streaming';
                draft.errorMessage = undefined;
                draft.errorDetail = undefined;
                draft.errorToken = undefined;
                draft.correlationId = correlationId;
                draft.ttftMs = undefined;
                draft.tokenCount = undefined;
                draft.latencyMs = undefined;
            });

            try {
                for await (const event of summarize(req, { signal: abort.signal, correlationId })) {
                    // Drop events from a superseded request. The async iterator
                    // could still hold a few events buffered when `cancel()` runs.
                    if (requestId !== activeRequestId) {
                        return;
                    }
                    applyEvent(set, event);
                    if (event.event === 'done' || event.event === 'error') {
                        break;
                    }
                }

                // Stream ended without `done` or `error` (server closed or
                // network drop). §5.4: treat as soft truncation.
                if (requestId === activeRequestId) {
                    set(function markTruncated(draft: AiOverviewState) {
                        if (draft.status === 'streaming') {
                            draft.status = 'truncated';
                            draft.latencyMs = elapsedSinceStreamStart();
                        }
                    });
                    activeAbort = undefined;
                }
                if (cacheKey) {
                    const snap = snapshotForCache(get());
                    if (snap) {
                        set(function writeCache(draft: AiOverviewState) {
                            cacheWrite(draft.summaryCache, cacheKey, snap);
                        });
                    }
                }
            } catch (err: unknown) {
                if (requestId !== activeRequestId) {
                    return;
                }
                if (isAbortError(err)) {
                    // User-initiated cancellation; leave state alone — a new
                    // `start()` is about to overwrite it, or `reset()` already did.
                    return;
                }
                logger.error('aiOverviewStore: summarize stream failed', err);
                const errorToken = errorTokenFromFailure(err);
                set(function markError(draft: AiOverviewState) {
                    draft.status = 'error';
                    draft.latencyMs = elapsedSinceStreamStart();
                    // The contract instructs the UI to surface its own copy
                    // rather than the raw server error string, and all current
                    // failure modes (5xx, network, malformed body) collapse to
                    // the same UX message. The taxonomy token, used only for
                    // telemetry, is derived from the HTTP status where present.
                    draft.errorMessage = 'unavailable';
                    draft.errorToken = errorToken;
                });
                activeAbort = undefined;
            }
        },

        cancel(): void {
            if (activeAbort) {
                activeAbort.abort();
                activeAbort = undefined;
            }
        },

        reset(): void {
            if (activeAbort) {
                activeAbort.abort();
                activeAbort = undefined;
            }
            activeRequestId++;
            // Reset the view slice only; the cache survives so a back-nav
            // to the same search context still short-circuits to a cache hit.
            set(function resetStore(draft: AiOverviewState) {
                Object.assign(draft, baseViewState());
            });
        },
    }),
    'aiOverviewStore',
    IS_DEVELOPMENT,
);

function applyEvent(set: SetState<AiOverviewState>, event: SummarizeStreamEvent): void {
    switch (event.event) {
        case 'ai_overview_chunk':
            set(function appendOverview(draft: AiOverviewState) {
                if (draft.ttftMs === undefined && streamStartedAt !== undefined) {
                    draft.ttftMs = performance.now() - streamStartedAt;
                }
                draft.overview += event.data;
                draft.tokenCount = estimateTokens(draft.overview);
            });
            break;
        case 'ai_overview_complete':
            set(function completeOverview(draft: AiOverviewState) {
                draft.overviewDone = true;
            });
            break;
        case 'listing_summary_chunk': {
            const { oafId, chunk } = event.data;
            set(function appendListing(draft: AiOverviewState) {
                const cur = draft.perListing[oafId];
                if (cur) {
                    cur.text += chunk;
                } else {
                    // Defensive: server sent a chunk for an oafId we did not
                    // request. Prefer dropping over silently fabricating UI.
                    logger.warn(`aiOverviewStore: chunk for unknown oafId "${oafId}"`);
                }
            });
            break;
        }
        case 'listing_summary_complete': {
            const { oafId } = event.data;
            set(function markListingDone(draft: AiOverviewState) {
                const cur = draft.perListing[oafId];
                if (cur) {
                    cur.done = true;
                }
            });
            break;
        }
        case 'next_queries':
            set(function setNextQueries(draft: AiOverviewState) {
                draft.nextQueries = event.data;
            });
            break;
        case 'done':
            set(function markDone(draft: AiOverviewState) {
                draft.status = 'done';
                draft.latencyMs = elapsedSinceStreamStart();
            });
            activeAbort = undefined;
            break;
        case 'error':
            // Map raw "Service unavailable" to a UX token; the contract
            // explicitly tells the UI not to surface the raw message.
            set(function markErrorEvent(draft: AiOverviewState) {
                draft.status = 'error';
                draft.latencyMs = elapsedSinceStreamStart();
                draft.errorMessage = 'unavailable';
                // Raw message kept for streamError telemetry only, never shown.
                draft.errorDetail = event.data.message;
                // The SSE error event carries no status code the client can
                // classify, so the telemetry token stays 'unknown'.
                draft.errorToken = AI_OVERVIEW_ERROR_TOKEN_FALLBACK;
            });
            activeAbort = undefined;
            break;
        default:
            // Forward-compatible: unknown event types are ignored so we can
            // ship UI before/after server adds new event kinds.
            break;
    }
}

function isAbortError(err: unknown): boolean {
    return !!err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError';
}

/**
 * Test-only: drop every cached summary. Product code should not call this —
 * `reset()` deliberately leaves the cache intact so that navigating away
 * (which resets the view) and returning still hits the cache. Uses
 * `setState` (not `.clear()`) because the in-state Map is immer-frozen.
 */
export const __clearAiOverviewCacheForTests = (): void => {
    aiOverviewStore.setState({ summaryCache: new Map() });
};

// ---------- Selectors ----------------------------------------------------

export const selectAiOverviewStatus = (s: AiOverviewState): AiOverviewStatus => s.status;
export const selectAiOverviewError = (s: AiOverviewState): string | undefined => s.errorMessage;
export const selectAiOverviewErrorDetail = (s: AiOverviewState): string | undefined => s.errorDetail;
export const selectAiOverviewErrorToken = (s: AiOverviewState): AiOverviewErrorToken | undefined => s.errorToken;
export const selectAiOverviewKeyword = (s: AiOverviewState): string => s.searchKeyword;
export const selectAiOverviewSummary = (s: AiOverviewState): { text: string; done: boolean } => ({
    text: s.overview,
    done: s.overviewDone,
});
export const selectAiOverviewListings = (s: AiOverviewState): SummarizeListing[] => s.listings;
export const selectAiOverviewPerListing = (s: AiOverviewState): Record<string, AiListingSummaryState> => s.perListing;
export const selectAiOverviewNextQueries = (s: AiOverviewState): string[] => s.nextQueries;
export const selectAiOverviewCorrelationId = (s: AiOverviewState): string => s.correlationId;
export const selectAiOverviewTtftMs = (s: AiOverviewState): number | undefined => s.ttftMs;
export const selectAiOverviewTokenCount = (s: AiOverviewState): number | undefined => s.tokenCount;
export const selectAiOverviewLatencyMs = (s: AiOverviewState): number | undefined => s.latencyMs;
